import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FillableFormEditor from "@/components/FillableFormEditor";
import { FileText, Loader2, PenSquare, Trash2, Upload, UserPlus, LayoutTemplate, Stethoscope, Tags } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TEMPLATE_CATEGORIES = [
  { value: "w4", label: "W-4" },
  { value: "i9", label: "I-9" },
  { value: "direct_deposit", label: "Direct Deposit" },
  { value: "handbook_ack", label: "Handbook Ack" },
  { value: "background_check", label: "Background Check" },
  { value: "assessment_admission", label: "RN Assessment — Admission" },
  { value: "assessment_supervisory", label: "RN Assessment — Supervisory Visit" },
  { value: "assessment_readmission", label: "RN Assessment — Re-Admission" },
  { value: "client_admission_form", label: "Admission Forms (client packet)" },
  { value: "other", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(TEMPLATE_CATEGORIES.map((c) => [c.value, c.label]));

function AssignDialog({ template, onClose, onAssigned }) {
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const needsRn = (template?.fillable_form?.fields || []).some((f) => f.assignee === "rn");
  const [rns, setRns] = useState([]);
  const [rnId, setRnId] = useState("any");

  useEffect(() => {
    axios.get(`${API}/employees`).then((r) => setEmployees(r.data || [])).catch(() => toast.error("Could not load employees"));
    if (needsRn) axios.get(`${API}/cosign-requests/meta/rns`).then((r) => setRns(r.data.rns || [])).catch(() => {});
  }, [needsRn]);

  const visible = useMemo(() => {
    const q = query.toLowerCase();
    return employees.filter((e) => (e.name || `${e.first_name} ${e.last_name}`).toLowerCase().includes(q));
  }, [employees, query]);

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const assign = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/form-templates/${template.id}/assign`, { employee_ids: [...selected], cosign_rn_id: needsRn && rnId !== "any" ? rnId : null });
      const skipped = res.data.skipped?.length || 0;
      toast.success(`Assigned to ${res.data.assigned_count} employee${res.data.assigned_count === 1 ? "" : "s"}${skipped ? ` · ${skipped} already had it` : ""}`);
      onAssigned?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-template-dialog">
        <DialogHeader>
          <DialogTitle>Assign “{template.title}”</DialogTitle>
        </DialogHeader>
        <Input placeholder="Search employees…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="assign-search" />
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{selected.size} selected</span>
          <button className="underline" onClick={() => setSelected(new Set(visible.map((e) => e.id)))} data-testid="assign-select-all">
            Select all shown
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border rounded-md">
          {visible.map((e) => (
            <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`assign-emp-${e.id}`} />
              <span className="flex-1 truncate">{e.name || `${e.first_name} ${e.last_name}`}</span>
              <span className="text-xs text-slate-400 capitalize">{(e.role || "").replace(/_/g, " ")}</span>
            </label>
          ))}
          {visible.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No employees match</p>}
        </div>
        <DialogFooter>
          {needsRn && (
            <div className="mr-auto flex items-center gap-2 text-xs text-slate-600" data-testid="assign-rn-picker">
              <Stethoscope className="w-4 h-4 text-rose-600" /> RN co-signer:
              <Select value={rnId} onValueChange={setRnId}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="assign-rn-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any RN</SelectItem>
                  {rns.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={assign} disabled={busy || selected.size === 0} className="gap-1 bg-purple-600 hover:bg-purple-700" data-testid="assign-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Assign to {selected.size || ""} employee{selected.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("w4");
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/form-templates`);
      setTemplates(r.data.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title || file.name.replace(/\.[^.]+$/, ""));
      form.append("category", category);
      const r = await axios.post(`${API}/form-templates`, form, { headers: { "Content-Type": "multipart/form-data" } });
      const n = r.data.fillable_form?.fields?.length || 0;
      toast.success(n ? `Template saved — ${n} fillable field${n === 1 ? "" : "s"} detected` : "Template saved — use “Edit fields” to draw the boxes");
      setTitle("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveCategories = async (t, categories) => {
    try {
      const r = await axios.patch(`${API}/form-templates/${t.id}`, { categories });
      setTemplates((list) => list.map((x) => (x.id === t.id ? { ...x, ...r.data } : x)));
      toast.success(`“${t.title}” now appears in ${(r.data.all_categories || []).map((c) => CAT_LABEL[c] || c).join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleRecurring = async (t, val) => {
    try {
      const r = await axios.patch(`${API}/form-templates/${t.id}`, { recurring_updates: val, recurring_count: t.recurring_count || 5 });
      setTemplates((list) => list.map((x) => (x.id === t.id ? { ...x, ...r.data } : x)));
      toast.success(val ? `“${t.title}” will be updated and re-signed at each supervisory visit (${t.recurring_count || 5} updates)` : `“${t.title}” is a single-fill form again`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };
  const saveRecurringCount = async (t, count) => {
    if (!count || count < 1 || count > 12) { toast.error("Number of updates must be 1–12"); return; }
    try {
      await axios.patch(`${API}/form-templates/${t.id}`, { recurring_count: count });
      toast.success(`“${t.title}”: ${count} supervisory updates per cycle`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleAutoAssign = async (t, val) => {
    try {
      await axios.patch(`${API}/form-templates/${t.id}`, { auto_assign_new_hires: val });
      setTemplates((list) => list.map((x) => (x.id === t.id ? { ...x, auto_assign_new_hires: val } : x)));
      toast.success(val ? `“${t.title}” will be given to every new hire automatically` : `“${t.title}” removed from new-hire forms`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template “${t.title}”? Already-assigned forms are not affected.`)) return;
    try {
      await axios.delete(`${API}/form-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="form-templates-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="w-5 h-5 text-purple-600" />
          <h2 className="font-semibold text-slate-900">Form Templates Library</h2>
        </div>
        <p className="text-sm text-slate-500">
          Configure a W-4, I-9 or any PDF once — fields, required marks and HR pre-fills — then assign it to any employee's “Forms to Fill” in one click. Flip <strong>New hires</strong> on and the form is handed out automatically the moment an employee is created.
        </p>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto] items-end">
          <div>
            <Label className="text-xs">Template title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 Federal W-4" data-testid="template-title-input" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label>
            <input type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={uploading} data-testid="template-file-input" />
            <Button asChild variant="default" className="gap-2 bg-purple-600 hover:bg-purple-700 cursor-pointer">
              <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload PDF template</span>
            </Button>
          </label>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="templates-empty">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No templates yet</p>
            <p className="text-sm mt-1">Upload a fillable PDF above, or open any configured form in an employee's Files tab and click “Save as template”.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Template</th>
                <th className="text-left px-4 py-3 font-medium">Fields</th>
                <th className="text-left px-4 py-3 font-medium">New hires</th>
                <th className="text-left px-4 py-3 font-medium">Assigned</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {templates.map((t) => {
                const n = t.fillable_form?.fields?.length || 0;
                const hr = (t.fillable_form?.fields || []).filter((f) => f.assignee === "hr").length;
                return (
                  <tr key={t.id} className="hover:bg-slate-50/60" data-testid={`template-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{t.title}</p>
                      <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                        {(t.all_categories || [t.category]).map((c) => (
                          <Badge key={c} variant="outline" className={`text-[10px] ${c === t.category ? "bg-slate-100" : "bg-sky-50 text-sky-700 border-sky-200"}`} data-testid={`template-cat-${t.id}-${c}`}>{CAT_LABEL[c] || c}</Badge>
                        ))}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" title="Use this form in more than one category" data-testid={`template-categories-btn-${t.id}`}>
                              <Tags className="w-3 h-3" /> Also use in…
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-2" align="start">
                            <p className="text-[11px] text-slate-500 mb-1 px-1">Show “{t.title}” under these categories too:</p>
                            <div className="max-h-64 overflow-y-auto">
                              {TEMPLATE_CATEGORIES.filter((c) => c.value !== t.category).map((c) => {
                                const on = (t.categories || []).includes(c.value);
                                return (
                                  <label key={c.value} className="flex items-center gap-2 px-1 py-1 text-xs hover:bg-slate-50 rounded cursor-pointer">
                                    <Checkbox checked={on} onCheckedChange={(v) => saveCategories(t, v ? [...(t.categories || []), c.value] : (t.categories || []).filter((x) => x !== c.value))} data-testid={`template-extra-cat-${t.id}-${c.value}`} />
                                    {c.label}
                                  </label>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <span>· {t.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {n > 0 ? (
                        <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                          {n} field{n === 1 ? "" : "s"}{hr ? ` · ${hr} HR pre-filled` : ""}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Needs fields</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <label className="flex items-center gap-2 text-xs text-slate-600" title={n === 0 ? "Configure fields first" : "Automatically assign when a new employee is created"}>
                        <Switch
                          checked={!!t.auto_assign_new_hires}
                          onCheckedChange={(val) => toggleAutoAssign(t, val)}
                          disabled={n === 0}
                          data-testid={`template-auto-assign-${t.id}`}
                        />
                        {t.auto_assign_new_hires ? "Auto" : "Off"}
                      </label>
                      {(t.all_categories || [t.category]).some((c) => /^(assessment_|client_admission_form)/.test(c || "")) && (
                        <label className="flex items-center gap-2 text-xs text-slate-600 mt-2" title="Re-sign and update this form at each supervisory visit (e.g. Plan of Care)">
                          <Switch
                            checked={!!t.recurring_updates}
                            onCheckedChange={(val) => toggleRecurring(t, val)}
                            disabled={n === 0}
                            data-testid={`template-recurring-${t.id}`}
                          />
                          {t.recurring_updates ? (
                            <span className="flex items-center gap-1">
                              Update at visits ×
                              <input
                                type="number" min={1} max={12}
                                className="w-12 h-6 border rounded px-1 text-xs"
                                value={t.recurring_count || 5}
                                onChange={(e) => setTemplates((list) => list.map((x) => (x.id === t.id ? { ...x, recurring_count: Number(e.target.value) } : x)))}
                                onBlur={(e) => saveRecurringCount(t, Number(e.target.value))}
                                data-testid={`template-recurring-count-${t.id}`}
                              />
                            </span>
                          ) : "Single fill"}
                        </label>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.assigned_count || 0} time{t.assigned_count === 1 ? "" : "s"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}>
                          <PenSquare className="w-3.5 h-3.5" /> Edit fields
                        </Button>
                        <Button size="sm" className="gap-1 bg-purple-600 hover:bg-purple-700" disabled={n === 0} onClick={() => setAssigning(t)} data-testid={`template-assign-${t.id}`}>
                          <UserPlus className="w-3.5 h-3.5" /> Assign
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <FillableFormEditor
          open
          onClose={() => setEditing(null)}
          file={{ id: editing.id, filename: editing.title, fillable_form: editing.fillable_form }}
          endpoints={{
            pdf: `${API}/form-templates/${editing.id}/file`,
            info: `${API}/form-templates/${editing.id}/pdf-info`,
            detect: `${API}/form-templates/${editing.id}/detect-fillable`,
            preview: `${API}/form-templates/${editing.id}/preview-values`,
          }}
          onSaveFields={async (fields) => { await axios.post(`${API}/form-templates/${editing.id}/fillable-form`, { fields }); }}
          onClearFields={async () => { await axios.delete(`${API}/form-templates/${editing.id}/fillable-form`); }}
          onSaved={load}
        />
      )}
      {assigning && <AssignDialog template={assigning} onClose={() => setAssigning(null)} onAssigned={load} />}
    </div>
  );
}
